import { BASE_URL } from "./configConstants";

/** Site origin without `/api` (static files usually live outside the API prefix). */
export function siteOriginWithoutApi() {
  return BASE_URL.replace(/\/+$/, "")
    .replace(/\/api\/?$/i, "")
    .replace(/\/+$/, "");
}

/** Laravel filesystem path segment → `/storage/...` style segment. */
export function normalizeVideoStoragePath(raw) {
  let p = String(raw ?? "").trim().replace(/^\/+/, "");
  if (!p) return "";
  return p.replace(/^storage\/app\/public\//i, "storage/");
}

/** Encode path segments so spaces / unicode still load (safely). */
export function encodePathSegments(pathWithoutLeadingSlash) {
  const raw = String(pathWithoutLeadingSlash ?? "").replace(/^\/+/, "");
  if (!raw) return "";
  return raw
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        try {
          return encodeURIComponent(segment);
        } catch {
          return segment;
        }
      }
    })
    .join("/");
}

/** If backend returns absolute URLs under `/api/…`, remap to site root paths. */
export function stripApiSegmentFromAbsoluteUrl(full) {
  const s = String(full ?? "").trim();
  if (!/^https?:\/\//i.test(s)) return s;
  try {
    const u = new URL(s);
    if (/^\/api(\/|$)/i.test(u.pathname)) {
      let nextPath = u.pathname.replace(/^\/api/i, "") || "/";
      u.pathname = nextPath.startsWith("/") ? nextPath : `/${nextPath}`;
      return u.href;
    }
  } catch {
    /* ignore */
  }
  return s;
}

/** Resolved URL for admin playback (single guess). */
export function resolveVideoHref(raw) {
  const list = getVideoPlaybackCandidates(raw);
  return list[0] ?? "#";
}

/** Join origin + relative path segment. */
function joinOriginPath(origin, rel) {
  const o = String(origin ?? "").replace(/\/+$/, "");
  const seg = String(rel ?? "").trim().replace(/^\/+/, "");
  if (!o || !seg) return "";
  return `${o}/${seg}`;
}

/**
 * Ordered unique URLs for `<video src>` — exhaustive fallbacks so admin still plays clips.
 */
export function getVideoPlaybackCandidates(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed || trimmed === "[object Object]") return [];

  const out = [];
  const add = (u) => {
    const v = String(u ?? "").trim();
    if (v && v !== "#" && !out.includes(v)) out.push(v);
  };

  const origin = siteOriginWithoutApi();
  const apiBase = BASE_URL.replace(/\/+$/, "");

  const pushRelativeVariants = (relPath) => {
    const stripped = String(relPath ?? "").replace(/^\/+/, "");
    if (!stripped) return;

    const normalized = normalizeVideoStoragePath(stripped);
    const encodedNorm = encodePathSegments(normalized);
    const encodedRaw = encodePathSegments(stripped);

    /* Public site (preferred) */
    add(joinOriginPath(origin, normalized));
    add(joinOriginPath(origin, stripped));
    if (encodedNorm && encodedNorm !== normalized) {
      add(joinOriginPath(origin, encodedNorm));
    }
    if (encodedRaw && encodedRaw !== stripped && encodedRaw !== encodedNorm) {
      add(joinOriginPath(origin, encodedRaw));
    }

    /* Laravel `public/` in URL on some hosts */
    if (!/^public\//i.test(stripped)) {
      add(joinOriginPath(origin, `public/${normalized}`));
      add(joinOriginPath(origin, `public/${stripped}`));
    }

    /* Filename-only (API sometimes returns just the file name) */
    if (/\.(mp4|webm|mov|mpeg|mkv)$/i.test(stripped) && !stripped.includes("/")) {
      add(joinOriginPath(origin, `storage/video/${stripped}`));
    }

    /* Some backends only expose files behind the API prefix */
    add(joinOriginPath(apiBase, normalized));
    add(joinOriginPath(apiBase, stripped));
    add(joinOriginPath(apiBase, `storage/${stripped.replace(/^storage\/?/i, "")}`));

    /* Double storage segment mistake from APIs */
    if (/^storage\//i.test(stripped)) {
      add(joinOriginPath(origin, stripped.replace(/^storage\/storage\//i, "storage/")));
    }
  };

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const u = new URL(trimmed);
      add(stripApiSegmentFromAbsoluteUrl(trimmed));
      add(trimmed);

      /* Same host paths as relative fallbacks */
      const pathPlusQuery = `${u.pathname}${u.search}${u.hash}`.replace(/^\//, "");
      pushRelativeVariants(pathPlusQuery);
      try {
        const decPath = decodeURIComponent(pathPlusQuery);
        if (decPath !== pathPlusQuery) pushRelativeVariants(decPath);
      } catch {
        /* invalid % escapes in pathname */
      }
    } catch {
      add(trimmed);
    }

    /* De-dupe preserves order */
    return out.filter(Boolean);
  }

  pushRelativeVariants(trimmed);
  try {
    const dec = decodeURIComponent(trimmed);
    if (dec !== trimmed) pushRelativeVariants(dec);
  } catch {
    /* ignore */
  }

  return out.filter(Boolean);
}
